/**
 * Home page hero (sections/cirque-hero.liquid).
 *
 * Crossfades each visible panel through its photos, one panel after the next, and sets
 * `--cq-hero-p` (0 at rest, 1 once the hero has scrolled away) for the parallax in CSS.
 */

const FADE_MS = 1700;

class CirqueHero extends HTMLElement {
  /** @type {{ frames: HTMLElement[], index: number, element: HTMLElement }[]} */
  #panels = [];

  /** @type {number | undefined} */
  #timer;

  /** Which visible panel changes next */
  #turn = 0;

  #onScreen = true;

  /** @type {number | null} */
  #scrollFrame = null;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  #observer = new IntersectionObserver(([entry]) => {
    this.#onScreen = Boolean(entry?.isIntersecting);
    this.#schedule();
  });

  connectedCallback() {
    this.#panels = Array.from(this.querySelectorAll('.cirque-hero__panel')).map((element) => ({
      element: /** @type {HTMLElement} */ (element),
      frames: /** @type {HTMLElement[]} */ (Array.from(element.querySelectorAll('.cirque-hero__frame'))),
      index: 0,
    }));

    this.#observer.observe(this);
    document.addEventListener('visibilitychange', this.#schedule);
    this.#reducedMotion.addEventListener('change', this.#schedule);

    if (this.dataset.parallax === 'true') {
      // Scroll events don't bubble, but capturing on the document hears every scroller,
      // whether the page or the theme's .page-wrapper is the one that scrolls.
      document.addEventListener('scroll', this.#handleScroll, { passive: true, capture: true });
      this.#updateParallax();
    }

    this.#schedule();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    document.removeEventListener('visibilitychange', this.#schedule);
    this.#reducedMotion.removeEventListener('change', this.#schedule);
    document.removeEventListener('scroll', this.#handleScroll, { capture: true });
    window.clearInterval(this.#timer);
    if (this.#scrollFrame !== null) cancelAnimationFrame(this.#scrollFrame);
  }

  /**
   * How far below the top of the screen the hero starts: 0 when it runs up under the header,
   * the header's height when it sits below it, and 0 again when it is further down the page.
   */
  get #restingTop() {
    let offset = 0;
    for (let node = /** @type {Element | null} */ (this); node instanceof HTMLElement; node = node.offsetParent) {
      offset += node.offsetTop;
    }

    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
    return offset <= headerHeight + 1 ? Math.max(offset, 0) : 0;
  }

  get #visiblePanels() {
    return this.#panels.filter((panel) => panel.frames.length > 1 && panel.element.offsetParent !== null);
  }

  /** Starts or stops the slideshow to match what the visitor can see. */
  #schedule = () => {
    window.clearInterval(this.#timer);
    this.#timer = undefined;

    const panels = this.#visiblePanels;
    const seconds = Number(this.dataset.interval) || 5;
    if (!this.#onScreen || document.hidden || this.#reducedMotion.matches || panels.length === 0) return;

    // Have each panel's next photo ready before its turn comes
    for (const panel of panels) this.#load(panel.frames[(panel.index + 1) % panel.frames.length]);

    // One panel changes per tick, so every panel still shows each photo for `seconds`
    this.#timer = window.setInterval(this.#tick, (seconds * 1000) / panels.length);
  };

  #tick = () => {
    const panels = this.#visiblePanels;
    if (panels.length === 0) return;

    // Each panel starts one photo ahead of the panel before it. Changing the last panel first,
    // then working back to the first, keeps every panel on a different photo at all times.
    // (Changing the first panel first would move it onto the photo its neighbor is still showing.)
    const panel = panels[panels.length - 1 - (this.#turn % panels.length)];
    this.#turn += 1;
    if (panel) this.#advance(panel);
  };

  /** @param {{ frames: HTMLElement[], index: number }} panel */
  async #advance(panel) {
    const current = panel.frames[panel.index];
    const nextIndex = (panel.index + 1) % panel.frames.length;
    const next = panel.frames[nextIndex];
    if (!current || !next) return;

    await this.#load(next);
    if (!this.isConnected) return;

    current.classList.add('is-leaving');
    current.classList.remove('is-active');
    next.classList.add('is-active');
    panel.index = nextIndex;

    window.setTimeout(() => current.classList.remove('is-leaving'), FADE_MS);
    this.#load(panel.frames[(nextIndex + 1) % panel.frames.length]);
  }

  /**
   * Gives a waiting photo its source and resolves once it can be painted.
   * @param {HTMLElement | undefined} frame
   */
  async #load(frame) {
    const image = frame?.querySelector('img');
    if (!image) return;

    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
      delete image.dataset.srcset;
    }
    if (image.dataset.src) {
      image.src = image.dataset.src;
      delete image.dataset.src;
    }

    try {
      await image.decode();
    } catch {
      // A photo that fails to decode still fades in; a broken image is better than a stuck show
    }
  }

  #handleScroll = () => {
    if (this.#scrollFrame !== null) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#updateParallax();
    });
  };

  #updateParallax() {
    if (this.#reducedMotion.matches) {
      this.style.removeProperty('--cq-hero-p');
      return;
    }

    const { top, height } = this.getBoundingClientRect();
    if (height === 0) return;

    // 0 while the hero sits where it starts, 1 once its bottom edge has reached the top of the screen
    const restingTop = this.#restingTop;
    const progress = Math.min(Math.max((restingTop - top) / (height + restingTop), 0), 1);

    this.style.setProperty('--cq-hero-p', progress.toFixed(4));
  }
}

if (!customElements.get('cirque-hero')) {
  customElements.define('cirque-hero', CirqueHero);
}
